<template>
  <div>
    <v-autocomplete
      v-model="selectedGrua"
      :items="gruaState.list"
      :loading="gruaState.loading"
      :search-input.sync="gruaState.search"
      :disabled="disabled"
      :rules="rules"
      :error-messages="errorMessages"
      :error="required && !selectedGrua"
      item-text="company"
      item-value="id"
      return-object
      label="Empresa de Grúa"
      prepend-inner-icon="mdi-truck"
      outlined
      dense
      clearable
      hide-no-data
      :filter="filterGruas"
    >
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-avatar size="24" class="mr-2">
            <v-icon small color="primary">mdi-truck</v-icon>
          </v-avatar>
          {{ displayGrua(item) }}
        </div>
      </template>

      <template v-slot:item="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="32" class="mr-3">
            <v-icon color="primary">mdi-truck</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-medium">{{ item.company }}</div>
            <div class="text-caption text--secondary">
              <span v-if="item.cif">CIF: {{ item.cif }}</span>
              <span v-if="item.phone"> • Tel: {{ item.phone }}</span>
              <span v-if="isCollaborator && item.dealership">
                • {{ typeof item.dealership === 'object' ? item.dealership.name : item.dealership }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:append-item v-if="showAddGruaButton">
        <v-divider class="mb-2"></v-divider>
        <div class="px-4 pb-2">
          <v-btn
            small
            color="primary"
            text
            @click="openCreateGruaDialog"
          >
            <v-icon left small>mdi-plus</v-icon>
            Crear nueva grúa
          </v-btn>
        </div>
      </template>

      <template v-slot:no-data>
        <div class="px-4 py-2 text-center" v-if="gruaState.search && gruaState.search.length > 2">
          <div class="text-subtitle-2 grey--text">No se encontraron grúas</div>
          <v-btn
            small
            color="primary"
            text
            class="mt-2"
            @click="openCreateGruaDialog"
          >
            <v-icon left small>mdi-plus</v-icon>
            Crear grúa
          </v-btn>
        </div>
      </template>
    </v-autocomplete>

    <!-- Diálogo para crear una nueva grúa -->
    <v-dialog v-model="createGruaDialog" max-width="600">
      <grua-form 
        @save="handleGruaCreated" 
        @cancel="createGruaDialog = false" 
        :created="true" 
      />
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import GruaService from '../services/GruaService'
import GruaForm from '../views/grua/GruaForm'

export default {
  name: 'GruaSelector',
  components: {
    GruaForm
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    currentDealerId: {
      type: [String, Number],
      default: null
    },
    lastMovement: {
      type: Object,
      default: null
    },
    isCompletingMovement: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedGrua: null,
      gruaState: {
        list: [],
        loading: false,
        search: ''
      },
      createGruaDialog: false
    }
  },
  computed: {
    isCollaborator() {
      return this.$store.getters.HAS_ROLE(['colaborator'])
    },
    showAddGruaButton() {
      if (!this.gruaState.search) {
        return false
      }
      const gruaExists = this.gruaState.list.some(grua =>
        grua.company.toLowerCase().includes(this.gruaState.search.toLowerCase())
      )
      return !gruaExists
    }
  },
  watch: {
    value: {
      handler(newValue) {
        this.selectedGrua = newValue
      },
      immediate: true
    },
    selectedGrua(newValue) {
      this.$emit('input', newValue)
      this.$emit('change', newValue)
    },
    'gruaState.search'(val) {
      this.gruaDebouncedSearch()
    }
  },
  async created() {
    await this.loadInitialGruas()
  },
  methods: {
    async loadInitialGruas() {
      this.gruaState.loading = true
      
      try {
        if (this.isCompletingMovement && this.lastMovement && this.lastMovement.crane_id) {
          // Si estamos completando un movimiento y hay un crane_id específico, cargar solo esa grúa
          const gruaParams = {
            id: this.lastMovement.crane_id,
            expand: 'dealership'
          }
          
          const { data } = await GruaService.view(gruaParams)
          this.gruaState.list = data.data ? [data.data] : []
          
          if (data.data) {
            this.selectedGrua = data.data
          }
        } else {
          // Cargar todas las grúas disponibles
          const gruaParams = { expand: 'dealership' }
          if (this.currentDealerId) {
            gruaParams['filter[dealer_id]'] = this.currentDealerId
          }
          
          const { data } = await GruaService.get(gruaParams)
          this.gruaState.list = data.data || []
          
          // Si hay una selectedGrua y no está en la lista, agregarla al principio
          if (this.selectedGrua && !this.gruaState.list.some(g => g.id === this.selectedGrua.id)) {
            this.gruaState.list.unshift(this.selectedGrua)
          }
        }
      } catch (error) {
        console.error('Error al cargar grúas:', error)
      } finally {
        this.gruaState.loading = false
      }
    },
    
    filterGruas(item, queryText, itemText) {
      const text = `${item.company} ${item.cif || ''} ${item.phone || ''}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    
    displayGrua(item) {
      const gruaInfo = item.company || ''
      
      let dealerInfo = ''
      if (this.isCollaborator) {
        if (item.dealership && item.dealership.name) {
          dealerInfo = ` (${item.dealership.name})`
        } else if (item.dealer && item.dealer.name) {
          dealerInfo = ` (${item.dealer.name})`
        } else if (item.dealer && typeof item.dealer === 'string') {
          dealerInfo = ` (${item.dealer})`
        } else if (item.dealer_name) {
          dealerInfo = ` (${item.dealer_name})`
        }
      }
      
      return gruaInfo + dealerInfo
    },
    
    gruaDebouncedSearch: _.debounce(async function () {
      if (this.gruaState.search && this.gruaState.search.length > 2) {
        this.gruaState.loading = true
        
        try {
          const gruaSearchParams = { 
            'filter[company]': this.gruaState.search,
            expand: 'dealership'
          }
          
          if (this.currentDealerId) {
            gruaSearchParams['filter[dealer_id]'] = this.currentDealerId
          }
          
          const { data } = await GruaService.get(gruaSearchParams)
          let gruas = data.data || []
          
          // Si tenemos dealer_id y es colaborador, filtrar manualmente si es necesario
          if (this.currentDealerId && this.isCollaborator) {
            gruas = gruas.filter(grua => 
              grua.dealership && grua.dealership.id === this.currentDealerId
            )
          }
          
          this.gruaState.list = gruas
        } catch (error) {
          console.error('Error al buscar grúas:', error)
        } finally {
          this.gruaState.loading = false
        }
      }
    }, 300),
    
    openCreateGruaDialog() {
      this.createGruaDialog = true
    },
    
    handleGruaCreated(event) {
      this.createGruaDialog = false
      const newGrua = event.value.data
      this.selectedGrua = newGrua
      this.gruaState.list.push(newGrua)
      this.gruaState.search = ''
      
      this.$emit('grua-created', newGrua)
    }
  }
}
</script>
